import imageCompression from 'browser-image-compression';
import { ImageOptimizationOptions, CompressionResult } from './types';
import { getPreset } from './presets';

export class ImageOptimizer {
  private options: ImageOptimizationOptions;

  constructor(options: ImageOptimizationOptions = {}) {
    this.options = {
      quality: 0.8,
      maxWidth: 1920,
      format: 'auto',
      progressive: true,
      preserveExif: false,
      ...options
    };

    // Apply preset settings if specified
    if (this.options.preset && this.options.preset !== 'custom') {
      const preset = getPreset(this.options.preset);
      if (preset?.image) {
        this.options = { ...preset.image, ...this.options };
      }
    }
  }

  async process(file: File): Promise<CompressionResult> {
    const startTime = performance.now();

    try {
      // Determine output format
      const outputFormat = this.getOutputFormat(file);
      
      // Configure compression options
      const compressionOptions = {
        maxSizeMB: undefined, // We'll use quality instead
        maxWidthOrHeight: Math.max(this.options.maxWidth || 1920, this.options.maxHeight || 1920),
        useWebWorker: true,
        fileType: outputFormat,
        initialQuality: this.options.quality || 0.8,
        alwaysKeepResolution: false,
        preserveExif: this.options.preserveExif || false,
        onProgress: (progress: number) => {
          this.options.onProgress?.(Math.round(progress * 100));
        }
      };

      // Compress the image
      const compressedFile = await imageCompression(file, compressionOptions);

      // Convert to blob if needed
      let blob: Blob;
      if (compressedFile instanceof Blob) {
        blob = compressedFile;
      } else {
        blob = new Blob([compressedFile], { type: outputFormat });
      }

      // Create URL for the compressed image
      const url = URL.createObjectURL(blob);

      // Calculate metrics
      const originalSize = file.size;
      const compressedSize = blob.size;
      const compressionRatio = Math.round(((originalSize - compressedSize) / originalSize) * 100);
      const processingTime = performance.now() - startTime;

      const result: CompressionResult = {
        originalSize,
        compressedSize,
        compressionRatio,
        processingTime,
        blob,
        url
      };

      this.options.onComplete?.(result);
      return result;

    } catch (error) {
      this.options.onError?.(error as Error);
      throw error;
    }
  }

  private getOutputFormat(file: File): string {
    if (this.options.format && this.options.format !== 'auto') {
      return this.getMimeType(this.options.format);
    }

    // Auto-detect best format based on input
    const inputType = file.type.toLowerCase();
    
    // For PNG with transparency, keep as PNG or convert to WebP
    if (inputType.includes('png')) {
      return 'image/webp'; // WebP supports transparency and better compression
    }
    
    // For JPEG, use WebP for better compression
    if (inputType.includes('jpeg') || inputType.includes('jpg')) {
      return 'image/webp';
    }
    
    // For other formats, default to WebP
    return 'image/webp';
  }

  private getMimeType(format: string): string {
    const mimeTypes: Record<string, string> = {
      'jpeg': 'image/jpeg',
      'jpg': 'image/jpeg',
      'png': 'image/png',
      'webp': 'image/webp',
      'avif': 'image/avif'
    };

    return mimeTypes[format.toLowerCase()] || 'image/jpeg';
  }

  async processMultiple(files: File[]): Promise<CompressionResult[]> {
    const results: CompressionResult[] = [];
    
    for (let i = 0; i < files.length; i++) {
      try {
        const result = await this.process(files[i]);
        results.push(result);
      } catch (error) {
        console.error(`Failed to process file ${files[i].name}:`, error);
        this.options.onError?.(error as Error);
      }
    }

    return results;
  }

  // Helper method to resize image to specific dimensions
  async resize(file: File, width: number, height: number): Promise<CompressionResult> {
    const originalOptions = { ...this.options };
    this.options.maxWidth = width;
    this.options.maxHeight = height;
    
    try {
      const result = await this.process(file);
      this.options = originalOptions; // Restore original options
      return result;
    } catch (error) {
      this.options = originalOptions; // Restore original options
      throw error;
    }
  }

  // Helper method to convert format
  async convertFormat(file: File, format: 'jpeg' | 'png' | 'webp' | 'avif'): Promise<CompressionResult> {
    const originalOptions = { ...this.options };
    this.options.format = format;
    
    try {
      const result = await this.process(file);
      this.options = originalOptions; // Restore original options
      return result;
    } catch (error) {
      this.options = originalOptions; // Restore original options
      throw error;
    }
  }
}
